import React, { useEffect, useRef, useState } from 'react';

import { Avatar, Button, Card, CardBody, CardHeader, Link, Separator, Text } from '@change/ds-core-ui';
import { ArrowUpIcon, EndorserBorderIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useI18n, withI18nPlaceholders } from '@change-corgi/core/react/i18n';

import I18N_PLACEHOLDERS from 'config/i18n/placeholders/petitionDetails/petition-details.yml';

const DescriptionSection: React.FC<DescriptionSectionProps> = ({
	description,
	textRef,
	hasExternalHandler,
	showFullDescription,
	expanded,
	shouldShowButton,
	handleToggle,
	getButtonLabel,
}) => {
	return (
		<>
			<Separator orientation="horizontal" className="border-stroke-accent-primary-weak my-5 opacity-34" />
			<CardBody className="p-0!">
				<Text
					as="p"
					ref={showFullDescription ? undefined : textRef}
					className={cn('leading-7', {
						'line-clamp-3': !showFullDescription && (hasExternalHandler || !expanded),
					})}
					expressive
				>
					{description}
				</Text>
				{shouldShowButton && handleToggle && (
					<Button
						onClick={handleToggle}
						className="text-typography-strong mt-4 w-fit p-0 font-normal"
						data-testid="endorsement-toggle-button"
						variant="tertiary"
						size="x-small"
					>
						<Text className="text-typography-strong text-md font-normal">{getButtonLabel()}</Text>
					</Button>
				)}
			</CardBody>
		</>
	);
};
const EndorsementCardComponent: React.FC<Props> = ({
	name,
	title,
	description,
	photoUrl,
	link,
	onReadMore,
	readMoreLabel,
	showFullDescription = false,
	className,
}) => {
	const textRef = useRef<HTMLParagraphElement>(null);
	const [isClamped, setIsClamped] = useState(false);
	const [expanded, setExpanded] = useState(false);

	const hasExternalHandler = Boolean(onReadMore);
	const { translate } = useI18n();

	useEffect(() => {
		if (!showFullDescription) {
			const descriptionElement = textRef.current;

			if (!descriptionElement) return;

			const clamped = descriptionElement.scrollHeight > descriptionElement.clientHeight + 1;
			setIsClamped(clamped);
		}
	}, [description, showFullDescription]);

	const handleToggle = hasExternalHandler ? onReadMore : () => setExpanded((prev) => !prev);

	const getButtonLabel = () => {
		if (hasExternalHandler && readMoreLabel) {
			return readMoreLabel;
		}
		return expanded
			? translate('corgi.petition_details.endorsements.show_less')
			: translate('corgi.petition_details.endorsements.read_full');
	};

	const shouldShowButton = (() => {
		if (showFullDescription) {
			return false;
		}
		if (hasExternalHandler) {
			return isClamped && Boolean(onReadMore && readMoreLabel);
		}
		return isClamped;
	})();

	const nameContent = (
		<Text variant="body" fontWeight="bold" size="large">
			{name}
		</Text>
	);

	return (
		<Card className={cn('gap-0! p-5!', className || 'bg-fill-accent-primary-weaker border-none')}>
			<CardHeader className="flex items-start justify-between p-0!">
				<div className="flex items-center gap-4">
					<div className="relative">
						<EndorserBorderIcon
							size={68}
							className="text-typography-accent-primary absolute top-[-8px] left-[-8px] sm:top-[-6px] sm:left-[-6px]"
						/>

						<Avatar className="min-h-[52px] min-w-[52px] border-2 border-white" size={52} alt={name} src={photoUrl} />
					</div>
					<div className="flex flex-col justify-between">
						{link ? (
							<Link href={link} variant="secondary" target="_blank">
								<div className="flex flex-row gap-1.5">
									{nameContent}
									<div className="rotate-45 [&_svg]:h-[12px] [&_svg]:w-[12px]">
										<ArrowUpIcon data-testid="arrow-up-icon" />
									</div>
								</div>
							</Link>
						) : (
							nameContent
						)}
						<Text variant="body" size="small" fontWeight="normal" className="text-typography-weak">
							{title}
						</Text>
					</div>
				</div>
			</CardHeader>
			{description && (
				<DescriptionSection
					description={description}
					textRef={textRef}
					hasExternalHandler={hasExternalHandler}
					showFullDescription={showFullDescription}
					expanded={expanded}
					shouldShowButton={shouldShowButton}
					handleToggle={handleToggle}
					getButtonLabel={getButtonLabel}
				/>
			)}
		</Card>
	);
};

type Props = {
	name: string;
	title: string;
	description?: string;
	photoUrl: string;
	link?: string;
	onReadMore?: () => void;
	readMoreLabel?: string;
	showFullDescription?: boolean;
	className?: string;
};

type DescriptionSectionProps = {
	description: string;
	textRef: React.RefObject<HTMLParagraphElement | null>;
	hasExternalHandler: boolean;
	showFullDescription: boolean;
	expanded: boolean;
	shouldShowButton: boolean;
	handleToggle: (() => void) | undefined;
	getButtonLabel: () => string;
};

export const EndorsementCard = withI18nPlaceholders(I18N_PLACEHOLDERS, EndorsementCardComponent);
